import { Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Observable, forkJoin, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AuthService } from 'src/app/core/services/auth.service';
import { Datatable, RequestDatatable, Column as ReportColumn, } from 'src/app/core/state/_models/report.models';
import { Relation } from 'src/app/core/state/_models/relation.models';
import { ChildTableColumn } from '../models/child-table.models';
import { SKIP_LOADER } from 'src/app/core/interceptors/http.interceptor';

/**
 * Payload column shape for getdatatabledata.
 * Matches the production DatatableComponent payload exactly
 * (confirmed by Mike's spike GABSW-1989).
 */
export interface PayloadColumn {
  data: string;
  name: string;
  searchable: boolean;
  orderable: boolean;
  search: { value: string; regex: boolean };
}

/**
 * Sync action item -- one entry in the syncdata array.
 * Uses `Name` (FieldName) for field identification since the backend
 * resolves fields by database column name (confirmed via curl testing).
 */
export interface SyncItem {
  ApplicationTableKey: string;
  ActionType: 'new' | 'update' | 'delete';
  ChildTempId?: string;
  Where?: { Rid?: number; Rids?: number[] };
  FieldsList?: Array<{ Name: string; Value: string; Base64EncodedFile?: string | null; Type?: string }>;
  IsBulkUpdateDelete?: boolean;
  LogTransaction?: boolean;
}

/**
 * Response from POST /api/formaction/syncdata.
 */
export interface SyncResponse {
  errors: number;
  new_recs: number;
  added_recs: Record<string, string>;
  updated_recs: number;
  deleted_recs: number;
  error_msg?: string[];
}

/**
 * ChildTableDataService
 *
 * Production data adapter that bridges the child table UI component
 * with the existing backend APIs. Uses the same endpoints and payload
 * shapes as the production DatatableComponent and FormActionController.
 *
 * This service is intentionally a thin wrapper -- it delegates to the
 * existing HTTP endpoints rather than going through NgRx, so we avoid
 * coupling to the store and can test independently.
 */
@Injectable({ providedIn: 'root' })
export class ChildTableDataService {
  private readonly apiUrl = environment.apiUrl;

  constructor(
    private readonly http: HttpClient,
    private readonly auth: AuthService,
  ) {}

  getApiUrl(): string { return this.apiUrl; }
  getAuthToken(): string { return localStorage.getItem('token') ?? ''; }

  // -------------------------------------------------------------------
  // Data Loading
  // -------------------------------------------------------------------

  /**
   * Load child table rows via POST /api/report/getdatatabledata.
   *
   * This is the same endpoint the production DatatableComponent uses.
   * Set IsEmbeddedReport=true for child tables embedded in forms.
   */
  loadRows(params: {
    tableKey: string;
    reportKey: string;
    referenceFieldKey?: string;
    lookupFieldValue?: string;
    start?: number;
    length?: number;
    draw?: number;
    searchValue?: string;
    orderColumn?: number;
    orderDir?: 'asc' | 'desc';
    columns?: PayloadColumn[];
    isEmbedded?: boolean;
  }): Observable<Datatable> {
    const payload: RequestDatatable = {
      TableKey: params.tableKey,
      ReportKey: params.reportKey,
      Draw: params.draw ?? 1,
      Start: params.start ?? 0,
      Length: params.length ?? 50,
      Order: [{ column: params.orderColumn ?? -1, dir: params.orderDir ?? 'asc' }],
      Columns: params.columns ?? [],
      Search: { value: params.searchValue ?? '', regex: false },
      IsGroupingEnabled: false,
      IsQueryReport: false,
      GetAll: false,
      FromDashboard: false,
      FromEmbeddedReport: false,
      Template: [],
      ConditionGroups: null,
    };

    if (params.isEmbedded) {
      payload.IsEmbeddedReport = true;
      payload.LookupFieldValue = params.lookupFieldValue ?? '';
      payload.ReferenceFieldKey = params.referenceFieldKey ?? '';
    }

    return this.http.post<Datatable>(
      `${this.apiUrl}/api/report/getdatatabledata`,
      payload,
    );
  }

  // -------------------------------------------------------------------
  // CRUD Operations
  // -------------------------------------------------------------------

  /**
   * Batch sync -- inserts, updates, and deletes in one call.
   * POST /api/formaction/syncdata
   *
   * This is the recommended endpoint for autosave.
   */
  syncChanges(items: SyncItem[]): Observable<SyncResponse> {
    return this.http.post<SyncResponse>(
      `${this.apiUrl}/api/formaction/syncdata`,
      items,
      {
        headers: this.auth.getHeaders(),
        context: new HttpContext().set(SKIP_LOADER, true),
      },
    );
  }

  /**
   * Insert a single new row.
   * POST /api/formaction/postdata
   */
  insertRow(
    tableKey: string,
    applicationKey: string,
    fields: Array<{ Key: string; Value: string; Base64EncodedFile?: string | null; Type?: string }>,
  ): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/api/formaction/postdata`,
      {
        ApplicationTableKey: tableKey,
        ApplicationKey: applicationKey,
        fieldsList: fields.map(f => ({
          Name: f.Key,
          Value: String(f.Value),
          Base64EncodedFile: f.Base64EncodedFile ?? null,
        })),
      },
      {
          headers: this.auth.getHeaders(),
          context: new HttpContext().set(SKIP_LOADER, true),
        },
    );
  }

  /**
   * Update one or more fields in a single row.
   * PUT /api/formaction/putdata
   */
  updateCell(
    tableKey: string,
    applicationKey: string,
    rid: number,
    fields: Array<{ Key: string; Value: string; Base64EncodedFile?: string | null; Type?: string }>,
  ): Observable<any> {
    return this.http.put(
      `${this.apiUrl}/api/formaction/putdata`,
      {
        ApplicationTableKey: tableKey,
        ApplicationKey: applicationKey,
        fieldsList: fields.map(f => ({
          Name: f.Key,
          Value: String(f.Value),
          Base64EncodedFile: f.Base64EncodedFile ?? null,
        })),
        Where: { Rid: rid },
      },
      {
          headers: this.auth.getHeaders(),
          context: new HttpContext().set(SKIP_LOADER, true),
        },
    );
  }

  /**
   * Delete one or more rows.
   * DELETE /api/formaction/deleteobject
   */
  deleteRows(
    tableKey: string,
    rids: number[],
  ): Observable<any> {
    const isBulk = rids.length > 1;
    const body: any = {
      ApplicationTableKey: tableKey,
      IsBulkUpdateDelete: isBulk,
      Where: isBulk ? { Rids: rids } : { Rid: rids[0] },
    };
    return this.http.delete(
      `${this.apiUrl}/api/formaction/deleteobject`,
      { headers: this.auth.getHeaders(), body },
    );
  }

  // -------------------------------------------------------------------
  // Relationship Discovery
  // -------------------------------------------------------------------

  /**
   * Get parent-child relationships for a table.
   * GET /api/relation/getbytableid?Id={tableId}
   */
  getRelations(parentTableKey: string): Observable<Relation[]> {
    return this.http.get<Relation[]>(
      `${this.apiUrl}/api/relation/getbytableid?Id=${encodeURIComponent(parentTableKey)}`,
    );
  }

  /**
   * Find the "Has many" relation for a specific child table.
   * Returns the Relation object or null if not found.
   *
   * For "Has many" relations returned by getbytableid:
   *   ApplicationTableKey  = child table (the one with the FK)
   *   ApplicationTable2Key = parent table (the one queried)
   */
  findChildRelation(
    parentTableKey: string,
    childTableKey: string,
  ): Observable<Relation | null> {
    return this.getRelations(parentTableKey).pipe(
      map(relations => {
        return relations.find(
          r => r.RelationType === 'Has many' &&
            r.ApplicationTableKey === childTableKey,
        ) ?? null;
      }),
    );
  }

  // -------------------------------------------------------------------
  // Form Management (for child table auto-forms)
  // -------------------------------------------------------------------

  /**
   * Create a form.
   * POST /api/Form
   */
  createForm(payload: any): Observable<any> {
    return this.http.post<any>(
      `${this.apiUrl}/api/Form`,
      payload,
    );
  }

  /**
   * Update an existing form.
   * PUT /api/Form
   */
  updateForm(payload: any): Observable<any> {
    return this.http.put<any>(
      `${this.apiUrl}/api/Form`,
      payload,
    );
  }

  // -------------------------------------------------------------------
  // Report Management (for child table auto-reports)
  // -------------------------------------------------------------------

  /**
   * Create a datatable report with specific columns.
   * POST /api/report/postdatatable
   */
  createReport(payload: any): Observable<any> {
    return this.http.post<any>(
      `${this.apiUrl}/api/report/postdatatable`,
      payload,
    );
  }

  /**
   * Update an existing datatable report.
   * PUT /api/report/postdatatable
   */
  updateReport(payload: any): Observable<any> {
    return this.http.put<any>(
      `${this.apiUrl}/api/report/postdatatable`,
      payload,
    );
  }

  // -------------------------------------------------------------------
  // Report/Field Metadata
  // -------------------------------------------------------------------

  /**
   * Get reports for a table.
   * GET /api/report/getreportbytableid?id={tableId}
   */
  getReports(tableKey: string): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.apiUrl}/api/report/getreportbytableid?id=${encodeURIComponent(tableKey)}&inForms=false&formType=&fromTableSettings=false`,
    );
  }

  /**
   * Get fields (columns) for a table.
   * GET /api/report/gettablefields?id={tableId}
   */
  getTableFields(tableKey: string): Observable<ReportColumn[]> {
    return this.http.get<ReportColumn[]>(
      `${this.apiUrl}/api/report/gettablefields?id=${encodeURIComponent(tableKey)}`,
    );
  }

  /**
   * Get columns selected for a specific report.
   * GET /api/report/getreportcolumns?id={reportKey}&tableId={tableKey}&fromDashboard=false
   */
  getReportColumns(reportKey: string, tableKey: string): Observable<ReportColumn[]> {
    return this.http.get<ReportColumn[]>(
      `${this.apiUrl}/api/report/getreportcolumns?id=${encodeURIComponent(reportKey)}&tableId=${encodeURIComponent(tableKey)}&fromDashboard=false`,
    );
  }

  /**
   * Get report details by ID/Key (includes FormKey).
   * GET /api/report/getreportbyid?id={reportKey}&tableId={tableKey}
   */
  getReportById(reportKey: string, tableKey: string): Observable<any> {
    return this.http.get<any>(
      `${this.apiUrl}/api/report/getreportbyid?id=${encodeURIComponent(reportKey)}&tableId=${encodeURIComponent(tableKey)}`
    ).pipe(catchError(() => of(null)));
  }

  // -------------------------------------------------------------------
  // Column Mapping Utilities
  // -------------------------------------------------------------------

  /**
   * Build the Columns payload for getdatatabledata from field metadata.
   *
   * Matches the production DatatableComponent's generatePayloadColumns()
   * (datatable.component.ts:819-898). Each entry uses field.FieldName as
   * the `data` key, plus one trailing empty entry.
   */
  buildPayloadColumns(fields: ReportColumn[]): PayloadColumn[] {
    const cols: PayloadColumn[] = fields.map(f => ({
      data: f.FieldName,
      name: '',
      searchable: true,
      orderable: true,
      search: { value: '', regex: false },
    }));
    cols.push({ data: '', name: '', searchable: true, orderable: true, search: { value: '', regex: false } });
    return cols;
  }

  /**
   * Convert production ReportColumn[] into ChildTableColumn[] for the UI.
   *
   * Handles the backend-to-frontend field type mapping and respects
   * formula/summary/lookup fields (marked read-only).
   *
   * @param fields   Field metadata from getTableFields()
   * @param dataKeyMap Optional map from FieldName to actual data key
   *                   (backend may prefix with tbl{id}_)
   */
  private static readonly SYSTEM_FIELD_NAMES = new Set([
    'createdby', 'modifiedby', 'datecreated', 'datemodified', 'id',
  ]);

  buildChildTableColumns(
    fields: ReportColumn[],
    dataKeyMap: Record<string, string> = {},
  ): ChildTableColumn<any>[] {
    return fields.map(field => {
      const isSystemField = ChildTableDataService.SYSTEM_FIELD_NAMES.has(field.FieldName.toLowerCase());
      const col: ChildTableColumn<any> = {
        key: dataKeyMap[field.FieldName] ?? field.FieldName,
        label: field.Name,
        type: this.mapFieldType(field.FieldType) as any,
        sortable: true,
        filterable: true,
        editable: !field.IsFormula && !field.IsSummaryField && !isSystemField,
        minWidth: 120,
      };

      if ((field.FieldType === 'ENUM' || field.FieldType === 'ENUM2') && field.Values?.length) {
        col.type = (field.FieldType === 'ENUM2' ? 'multiselect' : 'select') as any;
        (col as any).selectOptions = field.Values.map((v: any) => ({
          value: v.Value ?? v,
          label: v.Value ?? v,
        }));
      }

      return col;
    });
  }

  // -------------------------------------------------------------------
  // Dropdown & User Options
  // -------------------------------------------------------------------

  /**
   * Fetch dropdown values for a single ENUM/ENUM2 field.
   * GET /api/Field/getdropdownvalues?id={fieldKey}
   */
  fetchDropdownValues(fieldKey: string): Observable<DropdownOption[]> {
    return this.http.get<DropdownOption[]>(
      `${this.apiUrl}/api/Field/getdropdownvalues?id=${encodeURIComponent(fieldKey)}`,
    ).pipe(catchError(() => of([])));
  }

  /**
   * Batch-fetch dropdown values for multiple ENUM/ENUM2 fields.
   * Returns a map of fieldKey -> DropdownOption[].
   */
  fetchAllDropdownValues(fieldKeys: string[]): Observable<Record<string, DropdownOption[]>> {
    if (fieldKeys.length === 0) return of({});
    const calls: Record<string, Observable<DropdownOption[]>> = {};
    for (const key of fieldKeys) {
      calls[key] = this.fetchDropdownValues(key);
    }
    return forkJoin(calls);
  }

  /**
   * Fetch users for USER-type fields.
   * GET /api/Form/formfieldusers?tableId=...&applicationId=...
   */
  fetchUsers(tableKey: string, applicationId: string): Observable<DropdownOption[]> {
    const params = [
      `tableId=${encodeURIComponent(tableKey)}`,
      `applicationId=${encodeURIComponent(applicationId)}`,
      'isSearch=false', 'search=', 'isScroll=false',
      'skip=0', 'pageSize=0', 'isConditionComponent=false',
    ].join('&');
    return this.http.get<DropdownOption[]>(
      `${this.apiUrl}/api/Form/formfieldusers?${params}`,
    ).pipe(catchError(() => of([])));
  }

  // -------------------------------------------------------------------
  // Form & Rule Metadata
  // -------------------------------------------------------------------

  /**
   * Fetch forms for a table.
   * GET /api/Form/getbytableid?Id={tableKey}&inReport=false
   */
  fetchForms(tableKey: string): Observable<Array<{ Key: string; Name: string; Type?: string }>> {
    return this.http.get<Array<{ Key: string; Name: string; Type?: string }>>(
      `${this.apiUrl}/api/Form/getbytableid?Id=${encodeURIComponent(tableKey)}&inReport=false`,
    ).pipe(catchError(() => of([])));
  }

  /**
   * Fetch form rules for a given form + table.
   * GET /api/Form/getFormRules?id={formKey}&tableId={tableKey}
   */
  fetchFormRules(formKey: string, tableKey: string): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.apiUrl}/api/Form/getFormRules?id=${encodeURIComponent(formKey)}&tableId=${encodeURIComponent(tableKey)}`,
    ).pipe(catchError(() => of([])));
  }

  /**
   * Fetch saved fields for a form (includes static rules like Required, Readonly).
   * GET /api/Form/getformsavedfields?id={formKey}&tableId={tableKey}...
   */
  fetchFormSavedFields(formKey: string, tableKey: string, parentTableKey: string = '0'): Observable<any> {
    return this.http.get<any>(
      `${this.apiUrl}/api/Form/getformsavedfields?id=${encodeURIComponent(formKey)}&tableId=${encodeURIComponent(tableKey)}&viewRecord=true&defaultForm=true&fromEmbeddedOrDrilldown=true&parentTableId=${encodeURIComponent(parentTableKey)}`,
    ).pipe(catchError(() => of(null)));
  }

  // -------------------------------------------------------------------
  // Discovery (convenience)
  // -------------------------------------------------------------------

  /**
   * Discover reports + fields for a table in parallel.
   */
  discoverTable(tableKey: string): Observable<{
    reports: any[];
    fields: ReportColumn[];
    defaultReportKey: string | null;
  }> {
    return forkJoin({
      reports: this.getReports(tableKey),
      fields: this.getTableFields(tableKey),
    }).pipe(
      map(({ reports, fields }) => {
        const defaultReport = reports.find((r: any) => r.DefaultReport === 1) ?? reports[0] ?? null;
        return { reports, fields, defaultReportKey: defaultReport?.Key ?? null };
      }),
    );
  }

  /**
   * Map backend field type strings to child table column types.
   * Based on the Backend API Reference section 9 (Field Types).
   */
  private mapFieldType(fieldType: string): string {
    if (!fieldType) return 'text';
    const ft = fieldType.toUpperCase();

    if (ft.startsWith('DECIMAL')) return 'currency';
    if (ft === 'DOUBLE' || ft === 'FLOAT' || ft === 'BIGINT') return 'number';
    if (ft === 'INT' || ft.startsWith('INT(')) return 'integer';
    if (ft === 'TINYINT(1)') return 'checkbox';
    if (ft === 'ENUM') return 'select';
    if (ft === 'ENUM2') return 'multiselect';
    if (ft === 'FILE') return 'file';
    if (ft === 'DATE') return 'date';
    if (ft === 'DATETIME') return 'datetime';
    if (ft === 'TEXT') return 'textarea';
    if (ft === 'EMAIL') return 'email';
    if (ft === 'USER') return 'user';
    if (ft === 'PII') return 'pii';
    if (ft === 'PII2') return 'pii';
    if (ft === 'VARCHAR(15)') return 'phone';

    return 'text';
  }
}

/** Backend dropdown option shape shared by getdropdownvalues and formfieldusers */
export interface DropdownOption {
  Name: string | null;
  Value: string | null;
  IsActive?: boolean;
}
